// src/queries/trip.query.ts

import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import helpers from "helpers/index";
import queryClient from ".";
import { TypedJourney } from "./journey.query";

// Định nghĩa type cho object "seating" lồng bên trong
export interface ApiSeating {
    id: string;
    seating_available: string;
    seating_available_count: number;
    seating_name: string;
    seating_description: string;
    seating_number: number;
}

// Định nghĩa type cho mỗi chuyến đi (trip) trả về từ API
export interface TypedTripData {
    id?: string;
    journey_id?: string;
    departure_date?: string; /** Bigint */
    time?: string; // Giờ khởi hành, ví dụ: "05:00"
    price?: number;
    shipping_price?: number; // giá gửi hàng
    seating_id?: string;
    current_ticket_count?: number;
    createdAt?: string;
    // seating?: ApiSeating; /** đã bỏa ... */
    trip_manifest?: {
        id?: bigint,
        driver_settlement?: any,
        trip_type?: string, // mainline hooặc shuttle
        trip_manifest_parent?: bigint,
        total_occupied_seats?: number,
        total_occupied_good?: number,
        bonus_amount?: number,
        trip_status?: number,
        vehicle?: {
            license_plate: string
        },
        trip_manifest_driver?: {
            driver_role?: string;
            driver_data: {
                display_name: string
            }
        }[],
        seating?: ApiSeating,
        orders?: {
            id?: bigint,
            invoices?: {
                invoice_id?: bigint,
            }
        }[]
    },

    journey?: TypedJourney

}

export interface TypedTrip {
    data: TypedTripData[],
    meta?: {
        id: bigint,
        journey_id: bigint,
        departure_date: string,
    }
}

/**
* Hook mới để lấy danh sách các chuyến đi trong ngày theo journey và ngày đã chọn
* @param journeyId - ID của hành trình
* @param date - Ngày khởi hành
*/
export function useGetTripsByJourney(journeyId: string | bigint, date: Date) {
    // Format lại date để dùng trong URL
    date.setUTCHours(0, 0, 0, 0);
    const departureDate = date.getTime();

    const endpoint = `/trip/?journey_id=${journeyId}&departure_date=${departureDate}&limit=40`;

    return useQuery({ // Dữ liệu trả về là một mảng ApiTrip
        // queryKey phải phụ thuộc vào cả journeyId và ngày để tự động fetch lại khi thay đổi
        queryKey: ["trip/fetch_entity_list", journeyId, departureDate],
        queryFn: () => axios.get<TypedTrip>(endpoint).then(response => response.data),
        retry: 1,
        refetchOnMount: false,
        enabled: !!(journeyId) && !!departureDate
    });
}

/** Mutate  */
export function useMutateGetTripsByJourney() {
    return useMutation({ // Dữ liệu trả về là một mảng ApiTrip
        // queryKey phải phụ thuộc vào cả journeyId và ngày để tự động fetch lại khi thay đổi
        mutationKey: ["trip/fetch_entity_list"],
        mutationFn: ({ journey_id, date }: { journey_id: bigint, date: Date }): Promise<TypedTrip | null> => {
            // Format lại date để dùng trong URL
            date.setUTCHours(0, 0, 0, 0);
            const departureDate = date.getTime();
            const endpoint = `/trip/?journey_id=${journey_id}&departure_date=${departureDate}`;
            return axios.get<TypedTrip>(endpoint).then(response => response.data);
        },
        retry: 1,
    });
}


/**
 * Khối cập nhật thời gian ...
 * @returns 
 */

export function useMutateUpdateTimeToTrip() {
    return useMutation({
        mutationKey: ['triptime/update_entity'],
        mutationFn: ({ id, ...rest }: TypedTripData) => axios.patch<TypedTripData>(`/trip/${id}`, helpers.cleanEntity(rest)).then((response) => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}


export function useMutateAddTimeToTrip() {
    return useMutation({
        mutationKey: ['triptime/create_entity'],
        mutationFn: (entity: TypedTripData) => axios.post<TypedTripData>(`/trip/triptime`, helpers.cleanEntity(entity)).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** Chưa dùng nè? */
export function useMutateDeleteTrip() {
    return useMutation({
        mutationKey: ['triptime/delete_entity'],
        mutationFn: (id: any) => axios.delete<any>(`/trip/${id}`).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        }
    });
}

/** 
 * Chuyển sang giờ chạy khác
 * Cơ chế như sau nhá, nếu giờ có rồi thì nhét vào ID,còn chưa có thì tạo ID xong nhét vào ... done
 */
export function useMutateChangeTimeForTrip() {
    return useMutation({
        mutationKey: ['triptime/update_time_entity'],
        mutationFn: (body: { trip_id: string, time_string: string }) => axios.patch(`/trip/moving_time/${body.trip_id}`, { time_string: body.time_string }).then((response) => response.data),
        onSuccess: () => {
            /** Load lại trip */
            queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
            // queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity_list'] });
            queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity'] });
            // queryClient.invalidateQueries({ queryKey: ['ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị
            // queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });

        }
    });
}
